import React from "react";
import { motion } from "framer-motion";

function Module6() {
  return (
    <>
      <div className="bg-particles" />
      <div className="page-container">
        <motion.div
          className="module-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="module-number">Módulo 06 - Bonus</p>
          <h1 className="module-title">AgentCore Extras</h1>
          <p className="module-description">
            Primitivas avanzadas del ecosistema AgentCore: Identity, Observability,
            Policy, Evaluations, Code Interpreter, Browser, Payments y Registry.
            El sistema operativo completo para agentes en produccion.
          </p>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2>🆔 AgentCore Identity</h2>
          <p>
            Gestiona la identidad del agente y permite acceso delegado a recursos de terceros
            en nombre del usuario. Soporta OAuth2, IAM JWT tokens y API keys con flujos
            Machine-to-Machine (M2M) y delegacion de usuario (ON_BEHALF_OF).
          </p>
          <ul>
            <li><strong>M2M (Machine-to-Machine):</strong> El agente se autentica como si mismo ante servicios externos</li>
            <li><strong>USER_FEDERATION:</strong> El agente autentica al usuario via IdP externo (Cognito, Okta, Azure Entra)</li>
            <li><strong>ON_BEHALF_OF_TOKEN_EXCHANGE:</strong> El agente actua en nombre del usuario con token delegado</li>
            <li><strong>Credential Providers:</strong> Configurados en AgentCore para multiples IdPs</li>
          </ul>
          <div className="code-block">
{`# AgentCore Identity - Acceso delegado para Comfamito
from bedrock_agentcore.identity.auth import requires_access_token

@requires_access_token(
    provider_name="comfama-cognito",
    scopes=["profile:read", "benefits:read"],
    auth_flow="ON_BEHALF_OF_TOKEN_EXCHANGE"
)
async def consultar_perfil_afiliado(access_token: str, user_id: str):
    """Comfamito consulta el perfil del afiliado con token delegado."""
    headers = {"Authorization": f"Bearer {access_token}"}
    # El token fue obtenido automáticamente via Cognito
    # Comfamito solo puede acceder a lo que el usuario autorizo
    response = await httpx.get(
        f"https://api.comfama.internal/afiliados/{user_id}",
        headers=headers
    )
    return response.json()`}
          </div>
          <div className="highlight-box">
            <h4>Caso Comfama</h4>
            <p>
              Comfamito puede acceder al perfil del afiliado en sistemas internos de Comfama
              solo si el usuario ha dado consentimiento explicito. La identidad del agente
              se verifica en cada peticion con scopes granulares.
            </p>
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2>📊 AgentCore Observability</h2>
          <p>
            Tracing distribuido con OpenTelemetry nativo, dashboards automáticos en CloudWatch,
            y exportacion a Grafana, Datadog o Dynatrace. Cada tool call, LLM invocation y
            sesion genera spans OTel automáticamente.
          </p>
          <ul>
            <li><strong>OpenTelemetry Nativo:</strong> Spans automáticos para cada operacion del agente</li>
            <li><strong>ADOT (AWS Distro for OpenTelemetry):</strong> Serializacion y recoleccion de spans</li>
            <li><strong>Metricas Built-in:</strong> Token usage, latencia, session duration, error rates</li>
            <li><strong>Agent Span Collector:</strong> Recolecta spans desde CloudWatch Logs para evaluacion</li>
            <li><strong>Integraciones:</strong> CloudWatch, Grafana, Datadog, Dynatrace via OTel exporters</li>
          </ul>
          <div className="code-block">
{`# AgentCore Observability - Recoleccion de spans para evaluacion
from bedrock_agentcore.evaluation.agent_span_collector import AgentSpanCollector
from bedrock_agentcore.evaluation.span_to_adot_serializer import SpanToAdotSerializer

# Recolectar spans del agente Comfamito
collector = AgentSpanCollector(
    agent_id="comfamito-prod",
    region_name="us-east-1"
)

# Obtener spans de las ultimas 24 horas
spans = collector.collect_spans(
    start_time="2026-05-10T00:00:00Z",
    end_time="2026-05-11T00:00:00Z"
)

# Serializar para analisis
serializer = SpanToAdotSerializer()
for span in spans:
    adot_data = serializer.serialize(span)
    print(f"  Tool: {span.tool_name} | Latencia: {span.duration_ms}ms")
    print(f"  Tokens: {span.input_tokens} in / {span.output_tokens} out")`}
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2>🛡️ AgentCore Policy</h2>
          <p>
            Control granular sobre las acciones que el agente puede ejecutar usando
            politicas Cedar. Soporta generacion automatica de policies desde lenguaje
            natural y enforcement en tiempo real interceptando tool calls.
          </p>
          <ul>
            <li><strong>Policy Engine:</strong> Contenedor logico donde viven las politicas Cedar</li>
            <li><strong>Cedar Policies:</strong> permit / forbid con condiciones granulares sobre acciones</li>
            <li><strong>NL-to-Cedar:</strong> Genera politicas desde descripciónes en lenguaje natural</li>
            <li><strong>Real-time Enforcement:</strong> Intercepta cada tool call antes de ejecutarlo</li>
          </ul>
          <div className="code-block">
{`# AgentCore Policy - Controlar acciones de Comfamito con Cedar
from bedrock_agentcore.policy.client import PolicyEngineClient

client = PolicyEngineClient(region_name="us-east-1")

# Crear el engine de politicas
engine = client.create_policy_engine_and_wait(name="comfamito-policies")
engine_id = engine["policyEngineId"]

# Generar Cedar desde lenguaje natural
client.generate_and_create_policy(
    policy_engine_id=engine_id,
    description="Permitir a Comfamito consultar promociones solo para afiliados activos"
)

# Crear politica Cedar directamente
client.create_policy(
    policyEngineId=engine_id,
    policyBody="""
    permit(
      principal == Agent::"comfamito",
      action == Action::"invoke_tool",
      resource == Tool::"promociones"
    ) when { context.user.is_affiliate == true };

    forbid(
      principal == Agent::"comfamito",
      action == Action::"invoke_tool",
      resource == Tool::"admin_panel"
    );
    """
)`}
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
        >
          <h2>📈 AgentCore Evaluations</h2>
          <p>
            Evalua la calidad de agentes de forma continua en produccion. Muestrea sesiones
            automáticamente y aplica evaluadores built-in (correctness, helpfulness, safety)
            y custom. Soporta evaluacion a nivel SESSION, TRACE y TOOL_CALL.
          </p>
          <ul>
            <li><strong>Built-in Evaluators:</strong> CORRECTNESS, HELPFULNESS, SAFETY, FAITHFULNESS</li>
            <li><strong>Custom Evaluators:</strong> Define metricas de negocio propias</li>
            <li><strong>Reference Inputs:</strong> Ground truth con assertions y expected_trajectory</li>
            <li><strong>Online Evaluation:</strong> Muestreo automático en produccion con sampling rate</li>
            <li><strong>Batching:</strong> Hasta 10 evaluadores por invocacion</li>
          </ul>
          <div className="code-block">
{`# AgentCore Evaluations - Evaluar calidad de Comfamito
from bedrock_agentcore.evaluation.client import EvaluationClient, ReferenceInputs

eval_client = EvaluationClient(region_name="us-east-1")

# Crear evaluador built-in de correctitud
eval_client.create_evaluator_and_wait(
    name="comfamito-correctness",
    type="BUILTIN",
    evaluatorConfig={"builtIn": {"evaluatorType": "CORRECTNESS"}}
)

# Crear evaluador de safety
eval_client.create_evaluator_and_wait(
    name="comfamito-safety",
    type="BUILTIN",
    evaluatorConfig={"builtIn": {"evaluatorType": "SAFETY"}}
)

# Evaluar una sesion real del agente
results = eval_client.run(
    evaluator_ids=["comfamito-correctness", "comfamito-safety"],
    session_id="session-abc123",
    agent_id="comfamito-prod",
    reference_inputs=ReferenceInputs(
        assertions=[
            "La respuesta debe incluir información de subsidios",
            "No debe revelar datos internos de Comfama",
            "Debe responder en espanol"
        ]
    )
)
print(f"Correctness: {results['correctness']}")
print(f"Safety: {results['safety']}")`}
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h2>💻 AgentCore Code Interpreter</h2>
          <p>
            Ejecuta código Python en un sandbox seguro y aislado. Las sesiones persisten
            estado entre ejecuciónes, soportan instalacion de paquetes pip, manejo de archivos,
            y pueden conectarse a VPCs para acceso a datos internos.
          </p>
          <ul>
            <li><strong>Sandbox Aislado:</strong> Ejecucion segura sin acceso al sistema host</li>
            <li><strong>Context Persistence:</strong> Variables persisten entre llamadas a execute_code()</li>
            <li><strong>Paquetes Pip:</strong> Instala librerias como pandas, matplotlib, numpy</li>
            <li><strong>Archivos:</strong> Upload y download de archivos dentro del sandbox</li>
            <li><strong>VPC Support:</strong> Interpreters custom pueden conectarse a VPCs</li>
          </ul>
          <div className="code-block">
{`# AgentCore Code Interpreter - Comfamito calcula subsidios
from bedrock_agentcore.tools.code_interpreter_client import code_session

with code_session(region="us-east-1") as ci:
    # Instalar dependencias
    ci.install_packages(packages=["pandas", "matplotlib"])

    # Subir datos de afiliados
    ci.upload_file(path="/data/afiliados.csv", content=csv_data)

    # Ejecutar calculo de subsidios
    result = ci.execute_code(code="""
import pandas as pd
df = pd.read_csv('/data/afiliados.csv')

# Calcular subsidio promedio por estrato
subsidios = df.groupby('estrato')['subsidio_mensual'].mean()
print("Subsidio promedio por estrato:")
for estrato, valor in subsidios.items():
    print(f"  Estrato {estrato}: ` + "$" + `{valor:,.0f} COP")

total = df['subsidio_mensual'].sum()
print(f"\\nTotal subsidios mensuales: ` + "$" + `{total:,.0f} COP")
""")
    print(result.output)

    # Descargar archivo generado
    grafico = ci.download_file("/data/subsidios_chart.png")`}
          </div>
          <div className="highlight-box">
            <h4>Caso Comfama</h4>
            <p>
              Comfamito puede calcular subsidios personalizados, simular cuotas de credito,
              generar graficos de ahorro, y analizar datos del afiliado — todo ejecutando
              código Python seguro en tiempo real sin comprometer la infraestructura.
            </p>
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
        >
          <h2>🌐 AgentCore Browser</h2>
          <p>
            Navegador web serverless en sandbox para que agentes automaticen navegacion,
            extraigan contenido, y completen formularios. Incluye Live View para observar
            sesiones en tiempo real y reduccion automatica de CAPTCHAs.
          </p>
          <ul>
            <li><strong>Browser Sessions:</strong> Instancias aisladas con viewport configurable</li>
            <li><strong>Live View:</strong> URL pre-firmada para ver la sesion en tiempo real</li>
            <li><strong>Take/Release Control:</strong> Alternar entre automatizacion y control manual</li>
            <li><strong>Stream Control:</strong> Pausar/reanudar automatizacion bajo demanda</li>
            <li><strong>Recording:</strong> Grabacion de sesiones completas para replay y auditoria</li>
          </ul>
          <div className="code-block">
{`# AgentCore Browser - Comfamito navega la web
from bedrock_agentcore.tools.browser_client import browser_session

with browser_session(
    region="us-east-1",
    viewport={"width": 1280, "height": 720}
) as browser:
    session_id = browser.session_id

    # URL para ver la sesion en vivo (para auditoria)
    live_url = browser.generate_live_view_url(expires=3600)
    print(f"Live View: {live_url}")

    # Obtener WebSocket headers para frameworks de automatizacion
    ws_url, headers = browser.generate_ws_headers()

    # Control manual (para intervencion humana)
    browser.take_control()      # Pausa automatizacion
    # ... el humano puede interactuar via Live View ...
    browser.release_control()   # Reanuda automatizacion`}
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <h2>💳 AgentCore Payments</h2>
          <p>
            Permite a agentes procesar pagos de forma segura con gestion de instrumentos
            de pago, sesiones transaccionales, control presupuestario y autorizacion
            configurable. El agente puede cobrar dentro de limites pre-aprobados.
          </p>
          <ul>
            <li><strong>PaymentClient:</strong> Cliente para operaciones de pago individuales</li>
            <li><strong>PaymentManager:</strong> Orquesta workflows y estado transaccional completo</li>
            <li><strong>Budget Control:</strong> Validacion de presupuesto antes de ejecutar cobros</li>
            <li><strong>Payment Sessions:</strong> Sesiones con timeout, rollback y estado auditable</li>
            <li><strong>Authorizer Types:</strong> Configurable para aprobacion humana o automatica</li>
          </ul>
          <div className="code-block">
{`# AgentCore Payments - Comfamito procesa compras con puntos
from bedrock_agentcore.payments.client import PaymentClient
from bedrock_agentcore.payments.exceptions import InsufficientBudget

payment_client = PaymentClient(region_name="us-east-1")

async def comprar_con_puntos(user_id: str, item_id: str, puntos: int):
    """Comfamito procesa una compra en la Tienda con control de presupuesto."""
    try:
        # Verificar presupuesto disponible
        session = await payment_client.create_session(
            user_id=user_id,
            instrument_id="puntos-comfama",
            amount=puntos,
            currency="POINTS",
            description=f"Compra item {item_id} en Tienda Comfama"
        )

        # Ejecutar el cobro (dentro del budget aprobado)
        result = await payment_client.execute_payment(
            session_id=session.session_id
        )
        return f"Compra exitosa! {puntos} puntos debitados."

    except InsufficientBudget as e:
        return f"Puntos insuficientes. Disponibles: {e.available}"`}
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65 }}
        >
          <h2>📋 AgentCore Registry (Runtime)</h2>
          <p>
            El registro de agentes se maneja a traves del Runtime. Al hacer deploy, el agente
            queda registrado automáticamente y disponible para descubrimiento via A2A
            (Agent-to-Agent) y invocacion via AG-UI protocol con SSE streaming.
          </p>
          <ul>
            <li><strong>BedrockAgentCoreApp:</strong> Clase principal que registra el agente en el control plane</li>
            <li><strong>A2A Protocol:</strong> Descubrimiento e interoperabilidad entre agentes registrados</li>
            <li><strong>AG-UI Protocol:</strong> Endpoints SSE y WebSocket para invocacion streaming</li>
            <li><strong>Agent Discovery:</strong> Otros agentes descubren servicios por metadata semantica</li>
          </ul>
          <div className="code-block">
{`# AgentCore Registry - Registrar Comfamito como agente descubrible
from bedrock_agentcore.runtime.app import BedrockAgentCoreApp

app = BedrockAgentCoreApp()

@app.entrypoint
async def handler(request):
    """Entrypoint del agente Comfamito."""
    user_message = request.get("message", "")
    # Logica del agente aqui...
    return {
        "response": f"Hola! Soy Comfamito. Procesando: {user_message}",
        "metadata": {"agent": "comfamito", "version": "2.0"}
    }

# Al ejecutar 'agentcore deploy', el agente se registra automáticamente:
# - Queda descubrible via A2A por otros agentes
# - Recibe invocaciones via AG-UI con SSE streaming
# - Se le asigna identidad unica verificable

# Otros agentes pueden descubrir a Comfamito:
# agent_list = agentcore.discover(tags=["comfama", "afiliados"])
# comfamito = agent_list[0]
# response = await comfamito.invoke("Que beneficios tengo?")`}
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <h2>🗺️ Mapa Completo de AgentCore</h2>
          <div className="architecture-diagram">
            <div className="flow" style={{ flexDirection: "column", gap: "24px" }}>
              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", justifyContent: "center" }}>
                <div className="node">Runtime</div>
                <div className="node">Memory</div>
                <div className="node">Gateway</div>
              </div>
              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", justifyContent: "center" }}>
                <div className="node" style={{ borderColor: "#FF9900" }}>Identity</div>
                <div className="node" style={{ borderColor: "#FF9900" }}>Policy</div>
                <div className="node" style={{ borderColor: "#FF9900" }}>Observability</div>
                <div className="node" style={{ borderColor: "#FF9900" }}>Evaluations</div>
              </div>
              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", justifyContent: "center" }}>
                <div className="node" style={{ borderColor: "rgba(100,200,255,0.6)" }}>Code Interpreter</div>
                <div className="node" style={{ borderColor: "rgba(100,200,255,0.6)" }}>Browser</div>
                <div className="node" style={{ borderColor: "rgba(100,200,255,0.6)" }}>Payments</div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75 }}
        >
          <h2>📁 Archivos del Modulo</h2>
          <div className="code-block">
{`ejemplos/06_bonus_agentcore_extras/
├── README.md                          # Guia completa del modulo
├── 01_identity_demo.py                # Demo Identity con decoradores OAuth
├── 02_observability_spans.py          # Recoleccion y analisis de spans
├── 03_policy_cedar.py                 # Crear policies Cedar para Comfamito
├── 04_evaluations.py                  # Evaluar calidad del agente
├── 05_code_interpreter.py            # Ejecutar código en sandbox
├── 06_browser_tool.py                # Navegador serverless
├── 07_payments.py                    # Procesar pagos con control presupuestario
└── 08_registry_deploy.py            # Registro y descubrimiento A2A`}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <div className="source-links-banner">
            <h4>📖 Documentacion Oficial AWS</h4>
            <div className="source-links-list">
              <div className="source-link-item">
                <span>AgentCore →</span>
                <a href="https://docs.aws.amazon.com/bedrock/latest/userguide/agentcore.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/bedrock/latest/userguide/agentcore.html
                </a>
              </div>
              <div className="source-link-item">
                <span>Cedar Policies →</span>
                <a href="https://docs.cedarpolicy.com/" target="_blank" rel="noopener noreferrer">
                  docs.cedarpolicy.com
                </a>
              </div>
              <div className="source-link-item">
                <span>OpenTelemetry →</span>
                <a href="https://docs.aws.amazon.com/xray/latest/devguide/aws-xray.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/xray/latest/devguide/aws-xray.html
                </a>
              </div>
              <div className="source-link-item">
                <span>Amazon Cognito →</span>
                <a href="https://docs.aws.amazon.com/cognito/latest/developerguide/what-is-amazon-cognito.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/cognito/latest/developerguide/what-is-amazon-cognito.html
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}

export default Module6;
